import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTimes,
  faInfoCircle,
  faToggleOn,
  faDollarSign,
  faPalette,
  faEnvelope,
  faWrench,
  faUsers,
  faUserEdit,
  faEye,
  faEyeSlash,
  faList,
  faSearch,
  faSortUp,
  faSortDown,
  faTrash,
  faChevronUp,
  faChevronDown,
  faCopy,
  faFileExport,
  faTags,
} from "@fortawesome/free-solid-svg-icons";
import { useTheme, useUser } from "ogcommon";
import UsersTab from "./UsersTab";
import ScheduledReportsPanel from "./ScheduledReports"; // ← NEW
import TagsTab from "./TagsTab"; // ← NEW (same dir)
import DatabaseManager from "./DatabaseManager";

/* ══════════════════════════ component ══════════════════════════ */

// Optionally import axios if your "LeasesTab" needs it:
// import axios from "axios";

/**
 * This component now includes a "Leases" tab which displays and edits
 * the `leases` array returned by GET ?company=... from clientDetails.php.
 *
 * On save/patch, we send:
 * {
 *   ...formData,
 *   users,  // array
 *   leases, // array
 * }
 *
 * The server upserts them based on "LeaseID".
 */

// Utility to standardize input styles
const getInputClasses = (theme, { readOnly = false } = {}) => {
  return `
    w-full px-3 py-2 rounded outline-none border
    transition-colors duration-300
    ${readOnly ? "opacity-70 cursor-not-allowed" : ""}
    ${
      theme === "dark"
        ? "bg-gray-700 text-gray-100 border-gray-600 focus:ring-2 focus:ring-blue-600"
        : "bg-white text-gray-800 border-gray-300 focus:ring-2 focus:ring-blue-500"
    }
  `;
};

function attachLogsToClients(rawClients, rawLogs) {
  if (!Array.isArray(rawClients) || !Array.isArray(rawLogs)) {
    return []; // safest fallback
  }

  // 1️⃣  regroup logs by subdomain once (O(n))
  const logsBySub = rawLogs.reduce((tbl, log) => {
    const sub = (log.Subdomain || "").toLowerCase().trim();
    (tbl[sub] ??= []).push(log);
    return tbl;
  }, {});

  // 2️⃣  clone each client and tack on its logs (or [])
  return rawClients.map((cl) => ({
    ...cl,
    logs: logsBySub[(cl.name || "").toLowerCase().trim()] ?? [],
  }));
}

// ═══════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════
const CompanyDashboard = () => {
  const { theme } = useTheme();
  const CompanyLogs = React.lazy(() => import("./CompanyLogs"));

  /* master list straight from API */
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showActive, setShowActive] = useState(true);
  const { userRole, userID, setUser, companyName } = useUser();
  const [jobTypes, setJobTypes] = useState([]);

  // Gate for the Database Manager panel.
  // Exclusively available to the single super-access user whose UserID is
  // exactly "admin".  Do NOT use role as the primary gate.
  const isPrivilegedAdmin = userID === "admin";

  // DB Manager modal state — only used when isPrivilegedAdmin
  const [showDbModal,        setShowDbModal]        = useState(false);
  const [dbModalSubdomain,   setDbModalSubdomain]   = useState(null);
  const [dbModalInitialTab,  setDbModalInitialTab]  = useState("fieldticket");

  const openDbModal = (subdomain, tab) => {
    setDbModalSubdomain(subdomain || null);
    setDbModalInitialTab(tab || "fieldticket");
    setShowDbModal(true);
  };

  /*───────────────────────────────────────────────────────────────
   * 🔄 Auto-expand & smooth-scroll for newly added wells/tanks
   *   – place these inside your component (above the return).
   *──────────────────────────────────────────────────────────────*/

  /*───────────────────────────────────────────────────────────────
 * Wells & Tanks section – regenerated 2025-07-23
 *──────────────────────────────────────────────────────────────
  /* big modal state – unchanged from your original code */
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [formData, setFormData] = useState(() => defaultFormData());
  const [users, setUsers] = useState([]);
  const [leases, setLeases] = useState([]);
  const [purchasers, setPurchasers] = useState([]);
  const [tags, setTags] = useState([]);
  const [activeTab, setActiveTab] = useState("basic");
  const [auditLog, setAuditLog] = useState([]);
  const [useFieldTicket, setUseFieldTicket] = useState("");

  /* ──────────────────────────────────────────────────────────────────
     FETCH all companies (+ their nested logs) once on mount
     ────────────────────────────────────────────────────────────────── */
  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(
        "https://house.ogendeavors.com/api/clientDetails.php",
      );
      const js = await r.json();
      setCompanies(js); // raw – with their own logs & audits arrays
    } catch (err) {
      console.error("fetchCompanies:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();

    const saved = localStorage.getItem("showActive");
    if (saved !== null) setShowActive(JSON.parse(saved));
  }, [fetchCompanies]);

  /* 
     openEditForm   (called when a table row is clicked)
     ──────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────── */
  // openEditForm — hydrate formData, sync perms.UseFieldTicket and top-level UseFieldTicket
  const openEditForm = useCallback(async (row) => {
    setFormMode("edit");
    setLoading(true);

    try {
      const resp = await fetch(
        `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
          row?.subdomain || "",
        )}`,
      );
      const data = await resp.json();
      if (!Array.isArray(data) || !data.length) {
        throw new Error(
          `No data returned for “${row?.company || row?.subdomain}”`,
        );
      }
      const single = data[0];

      setAuditLog(Array.isArray(single.auditLog) ? single.auditLog : []);

      // start from defaults, overlay server values
      const fresh = { ...defaultFormData() };
      Object.keys(fresh).forEach((k) => {
        if (single[k] !== undefined) fresh[k] = single[k];
      });

      // parse perms
      let perms = {};
      if (typeof single.perms === "string") {
        try {
          perms = JSON.parse(single.perms);
        } catch {
          perms = {};
        }
      } else if (single.perms && typeof single.perms === "object") {
        perms = single.perms;
      }

      // normalize UseFieldTicket only (no generic helpers)
      const uft =
        perms && Object.prototype.hasOwnProperty.call(perms, "UseFieldTicket")
          ? perms.UseFieldTicket === true || perms.UseFieldTicket === "Y"
            ? "Y"
            : "N"
          : "N";

      // ensure perms object reflects Y/N for UseFieldTicket
      fresh.perms = { ...perms, UseFieldTicket: uft };
      // keep legacy top-level in sync
      fresh.UseFieldTicket = uft;

      setFormData(fresh);
      setUseFieldTicket(uft);

      setJobTypes(Array.isArray(single.jobTypes) ? single.jobTypes : []);
      setUsers(Array.isArray(single.users) ? single.users : []);
      setLeases(Array.isArray(single.leases) ? single.leases : []);
      setTags(Array.isArray(single.tags) ? single.tags : []);
      setPurchasers(Array.isArray(single.purchasers) ? single.purchasers : []);

      setActiveTab("basic");
      setShowForm(true);
    } catch (err) {
      console.error("openEditForm:", err);
      alert("Could not open client – see console for details.");
    } finally {
      setLoading(false);
    }
  }, []);

  // --------------------------------------------------------------------

  /* ──────────────────────────────────────────────────────────────
   openEditForm  –  launches the big “Edit Company” modal
   • row           – table row that was clicked; must contain .company
   • always calls the live API for the freshest data
   ──────────────────────────────────────────────────────────────*/
  // default form fields
  // --------------------------------------------------------------------
  function defaultFormData() {
    return {
      subdomain: "",
      name: "",
      user: "",
      pwd: "",
      company: "",
      useBSW: "N",
      use24: "N",
      useMaps: "N",
      useLinePressure: "N",
      ShowWells: "N",
      Active: "Y",
      Notes: "",
      UseWater: "Y",
      UseGas: "Y",
      Type: "O",
      ShowWellPropertyNum: "N",
      OperatorNum: "",
      PumperEditDaysBack: 0,
      UseWaterTanks: "N",
      ShowZeros: "N",
      UseColorCut: "N",
      AppName: "ogPumper",
      AppLogo: "http://ogPumper.com/images/logo.jpg",
      AppAuthor: "ogEndeavors, LLC",
      SupportEmail: "support@ogPumper.com",
      SupportPhone: "325-261-0394",
      SupportAddress: "ogEndeavors, LLC, PO Box 7091, Abilene, TX 79608",
      Bill: "Y",
      Rate1: 5.0,
      Rate2: 5.0,
      Rate3: 5.0,
      GaugeImportFile: "N",
      NetInterestBbls: "N",
      PR: "N",
      ShowPOC: "N",
      CurrentCustomCol: "LeaseID",
      CurrentCustomCol2: "PumperID",
      ShowLoads: "N",
      BillQuarterly: "N",
      ManualWaterLabel: "Water Bbls Gauged",
      ManualWaterLabel2: "*Enter bbls if not entering gauges",
      Data: "N",
      RequireWellReason: "N",
      UseImages: "N",
      PRStartDay: 2,
      CurrentLayout: "O",
      AlwaysOverrideRT: "N",
      ColorCutLabel: "Color Cut Gauge",
      InjCustomCol1: "LeaseID",
      InjCustomCol2: "PumperID",
      NotifyEmails: "",
      lineheightinches: "",
      UseFieldTicket: "N",
      UseBradenheadPressure: "N",
      perms: JSON.stringify({ hasOgFiles: false, UseFieldTicket: false }),
    };
  }

  // --------------------------------------------------------------------
  // close the main form
  // --------------------------------------------------------------------
  function closeForm() {
    setShowForm(false);
    setFormMode("create");
    setFormData(defaultFormData());
    setUsers([]);
    setLeases([]);
    setActiveTab("basic");
  }

  // --------------------------------------------------------------------
  // Create (POST)
  // --------------------------------------------------------------------
  // --------------------------------------------------------------------
  // Create (POST) – keeps DB creds for true tenant creation flows
  // Stringifies perms if it's an object
  // --------------------------------------------------------------------
  const handleCreate = useCallback(async () => {
    try {
      const payload = { ...formData, users, leases };
      if (payload && typeof payload.perms === "object") {
        payload.perms = JSON.stringify(payload.perms);
      }

      const response = await fetch(
        "https://house.ogendeavors.com/api/clientDetails.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        const msg = await response.text().catch(() => response.statusText);
        throw new Error(`Create failed: ${response.status} ${msg}`);
      }

      await response.json().catch(() => null); // ignore body if not needed
      fetchCompanies();
      closeForm();
    } catch (err) {
      console.error(err);
      alert(err.message || "Create failed. See console for details.");
    }
  }, [formData, users, leases, fetchCompanies]);

  // --------------------------------------------------------------------
  // Update (PATCH) – strictly client-level:
  //   • Strips tenant DB fields (name/user/pwd)
  //   • Stringifies perms if it's an object
  //   • Targets ?company=<subdomain>
  // --------------------------------------------------------------------
  const handleUpdate = useCallback(async () => {
    try {
      const { name, user, pwd, ...clientOnly } = formData; // drop tenant creds
      const payload = { ...clientOnly, users, leases };
      if (payload && typeof payload.perms === "object") {
        payload.perms = JSON.stringify(payload.perms);
      }

      const url = `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
        formData.subdomain,
      )}`;

      const response = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const msg = await response.text().catch(() => response.statusText);
        throw new Error(`Update failed: ${response.status} ${msg}`);
      }

      await response.json().catch(() => null); // ignore body if not needed
      fetchCompanies();
      closeForm();
    } catch (err) {
      console.error(err);
      alert(err.message || "Update failed. See console for details.");
    }
  }, [formData, users, leases, fetchCompanies]);

  // --------------------------------------------------------------------
  // Handle "Submit" in the main modal
  // --------------------------------------------------------------------
  const handleSubmitForm = () => {
    if (formMode === "create") {
      handleCreate();
    } else {
      handleUpdate();
    }
  };

  // --------------------------------------------------------------------
  // Toggle "active" vs "inactive" in top bar
  // --------------------------------------------------------------------
  const handleToggleActive = useCallback(() => {
    setShowActive((prev) => {
      const newVal = !prev;
      localStorage.setItem("showActive", JSON.stringify(newVal));
      return newVal;
    });
  }, []);

  // --------------------------------------------------------------------
  // Search + Active filtering
  // --------------------------------------------------------------------
  const filteredCompanies = useMemo(() => {
    const filteredByActive = showActive
      ? companies.filter((co) => co.Active === "Y")
      : companies.filter((co) => co.Active !== "Y");

    if (!searchQuery) return filteredByActive;

    const lower = searchQuery.toLowerCase();
    return filteredByActive.filter(
      (c) =>
        (c.company || "").toLowerCase().includes(lower) ||
        (c.subdomain || "").toLowerCase().includes(lower),
    );
  }, [companies, showActive, searchQuery]);

  // --------------------------------------------------------------------
  // Generic field change handler (supports object passthrough for `perms`)
  // --------------------------------------------------------------------
  const handleFieldChange = (e) => {
    const { name, value } = e.target ?? {};
    // If a child passes an object (e.g., BasicInfoTab sends perms as object),
    // accept it as-is. Otherwise use the event value.
    const next = name === "perms" && typeof value === "object" ? value : value;
    setFormData((prev) => ({ ...prev, [name]: next }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: checked ? "Y" : "N" };

      if (name === "UseFieldTicket") {
        const p = normaliseClientPerms(prev.perms);
        next.perms = { ...p, UseFieldTicket: checked }; // boolean
      }
      return next;
    });
  };

  const useFieldTicketBool = useMemo(
    () => normaliseClientPerms(formData.perms).UseFieldTicket,
    [formData.perms],
  );

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${
        theme === "dark"
          ? "bg-gray-900 text-gray-100"
          : "bg-gray-100 text-gray-900"
      }`}
    >
      <div className="container mx-auto px-4 py-8">
        {/* Title + Search + Active/Inactive Toggle */}
        <div className="w-full max-w-4xl mx-auto mb-6">
          <div
            className={`p-6 text-center rounded-xl shadow-md relative ${
              theme === "dark"
                ? "bg-gradient-to-r from-gray-800 to-gray-700"
                : "bg-gradient-to-r from-blue-500 to-indigo-500"
            }`}
          >
            <h2 className="text-3xl font-bold mb-4 text-white">
              Company Dashboard
            </h2>
            <div className="absolute top-4 right-4">
              <button
                onClick={handleToggleActive}
                className="inline-flex items-center justify-center font-bold py-2 px-4 rounded-full shadow-lg transition-transform duration-200 ease-in-out hover:scale-105 bg-white hover:bg-gray-200 text-blue-600"
              >
                <FontAwesomeIcon
                  icon={showActive ? faEye : faEyeSlash}
                  className="mr-2"
                />
                {showActive ? "Active" : "Inactive"}
              </button>
            </div>
            <div className="mb-6 flex flex-col sm:flex-row items-center sm:justify-center gap-4 mt-4">
              {/* Search input */}
              <input
                type="text"
                placeholder="Search by company name..."
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`
                  w-full max-w-md px-4 py-2 rounded-lg shadow-md focus:outline-none
                  focus:ring-2 transition-all duration-300
                  ${
                    theme === "dark"
                      ? "bg-gray-700 text-gray-100 placeholder-gray-400 border-gray-600 focus:ring-blue-600"
                      : "bg-white text-gray-800 placeholder-gray-500 border-gray-300 focus:ring-blue-500"
                  }
                `}
              />
              {/* Create New Company — admin only, opens provisioning modal */}
              {isPrivilegedAdmin && (
                <button
                  onClick={() => openDbModal(null, "create")}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-md bg-green-600 hover:bg-green-700 text-white transition-colors duration-200 whitespace-nowrap"
                >
                  <FontAwesomeIcon icon={faPlus} />
                  New Company
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Companies list */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredCompanies.length === 0 ? (
          <p className="text-center text-gray-500 dark:text-gray-300">
            No matches found.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCompanies.map((co) => (
              <div
                key={co.subdomain}
                className={`flex flex-col justify-between p-4 rounded-lg shadow-md border transform transition-all duration-200 hover:scale-105 ${
                  theme === "dark"
                    ? "bg-gray-800 hover:bg-gray-700 text-gray-200 border-gray-700"
                    : "bg-white hover:bg-gray-100 text-gray-800 border-gray-200"
                }`}
              >
                <div>
                  <h3 className="font-bold text-xl mb-1">
                    {co.company || "Unknown Company"}
                  </h3>
                  <p
                    className={`text-sm ${theme === "dark" ? "text-gray-300" : "text-gray-500"}`}
                  >
                    Subdomain: {co.subdomain}
                  </p>
                  <p className="text-sm mt-1">
                    Status:{" "}
                    <span
                      className={`${co.Active === "Y" ? "text-green-600" : "text-red-600"}`}
                    >
                      {co.Active === "Y" ? "Active" : "Inactive"}
                    </span>
                  </p>
                </div>
                <div className="mt-3 flex space-x-2">
                  {/* EDIT button -> triggers openEditForm */}
                  <button
                    onClick={() => openEditForm(co)}
                    className="flex-1 rounded-full py-2 font-semibold text-center bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    <FontAwesomeIcon icon={faEdit} className="mr-2" />
                    Edit
                  </button>
                  {/* DB Tools button — admin only */}
                  {isPrivilegedAdmin && (
                    <button
                      onClick={() => openDbModal(co.subdomain, "fieldticket")}
                      title="Database Tools"
                      className={`rounded-full py-2 px-3 font-semibold text-center transition-colors duration-200 ${
                        theme === "dark"
                          ? "bg-gray-700 hover:bg-gray-600 text-gray-200"
                          : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                      }`}
                    >
                      <FontAwesomeIcon icon={faWrench} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Database Manager modal — per-tenant, admin only */}
      {showDbModal && isPrivilegedAdmin && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-60 p-4">
          <div
            className={`w-full max-w-4xl rounded-xl shadow-2xl overflow-y-auto max-h-[90vh] relative ${
              theme === "dark" ? "bg-gray-800" : "bg-white"
            }`}
          >
            {/* Modal close button */}
            <button
              onClick={() => setShowDbModal(false)}
              className={`absolute top-3 right-3 z-10 p-1.5 rounded-full transition-colors ${
                theme === "dark"
                  ? "text-gray-400 hover:text-gray-100 hover:bg-gray-700"
                  : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
              }`}
              title="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <DatabaseManager
              tenantSubdomain={dbModalSubdomain}
              initialTab={dbModalInitialTab}
            />
          </div>
        </div>
      )}

      {/* Main Modal for create/edit a single company */}
      {showForm && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div
            className={`w-full max-w-5xl mx-auto p-6 rounded-lg shadow-2xl relative transition-colors duration-300 ${
              theme === "dark" ? "bg-gray-800 text-gray-100" : "bg-white"
            }`}
          >
            {/* Header row */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-4">
                <h2 className="text-2xl font-bold">
                  {formMode === "create"
                    ? "Create New Company"
                    : "Edit Company"}
                </h2>
              </div>
              {formMode === "edit" && (
                <a
                  href={`https://${formData.subdomain}.${formData.AppName}.com`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-block font-semibold text-lg px-3 py-1 rounded ${
                    theme === "dark"
                      ? "bg-gray-700 text-gray-100"
                      : "bg-gray-200 text-gray-800"
                  } hover:underline`}
                >
                  {formData.subdomain}.{formData.AppName}.com
                </a>
              )}

              <button
                onClick={closeForm}
                className="p-2 rounded-full text-gray-600 hover:text-gray-900 dark:hover:text-gray-200"
              >
                <FontAwesomeIcon icon={faTimes} size="lg" />
              </button>
            </div>

            {/* Tab bar */}
            <div className="mb-4 border-b border-gray-300 dark:border-gray-600 flex flex-wrap gap-2">
              <TabButton
                label="Basic Info"
                icon={faInfoCircle}
                active={activeTab === "basic"}
                onClick={() => setActiveTab("basic")}
              />
              <TabButton
                label="Features"
                icon={faToggleOn}
                active={activeTab === "features"}
                onClick={() => setActiveTab("features")}
              />
              <TabButton
                label="Billing"
                icon={faDollarSign}
                active={activeTab === "billing"}
                onClick={() => setActiveTab("billing")}
              />
              <TabButton
                label="Layout/Labels"
                icon={faPalette}
                active={activeTab === "layout"}
                onClick={() => setActiveTab("layout")}
              />
              <TabButton
                label="Support"
                icon={faEnvelope}
                active={activeTab === "support"}
                onClick={() => setActiveTab("support")}
              />
              <TabButton
                label="Misc"
                icon={faWrench}
                active={activeTab === "misc"}
                onClick={() => setActiveTab("misc")}
              />
              <TabButton
                label="Users"
                icon={faUsers}
                active={activeTab === "users"}
                onClick={() => setActiveTab("users")}
              />
              <TabButton
                label="Leases"
                icon={faList}
                active={activeTab === "leases"}
                onClick={() => setActiveTab("leases")}
              />
              <TabButton
                label="Tags"
                icon={faTags}
                active={activeTab === "tags"}
                onClick={() => setActiveTab("tags")}
              />
              <TabButton
                label="Logs"
                icon={faList}
                active={activeTab === "logs"}
                onClick={() => setActiveTab("logs")}
              />
              <TabButton
                label="Scheduled Reports"
                icon={faList}
                active={activeTab === "schedules"}
                onClick={() => setActiveTab("schedules")}
              />
            </div>

            {/* Tab content */}
            <div
              className={`max-h-[60vh] overflow-y-auto p-2 rounded border ${
                theme === "dark" ? "border-gray-600" : "border-gray-200"
              }`}
            >
              {activeTab === "basic" && (
                <BasicInfoTab
                  formData={formData}
                  onChange={handleFieldChange}
                  onToggle={handleCheckboxChange}
                  theme={theme}
                  formMode={formMode}
                />
              )}
              {activeTab === "features" && (
                <FeaturesTab
                  formData={formData}
                  onToggle={handleCheckboxChange}
                  onChange={handleFieldChange}
                  theme={theme}
                />
              )}
              {activeTab === "billing" && (
                <BillingTab
                  formData={formData}
                  onChange={handleFieldChange}
                  onToggle={handleCheckboxChange}
                  theme={theme}
                />
              )}
              {activeTab === "layout" && (
                <LayoutTab
                  formData={formData}
                  onChange={handleFieldChange}
                  theme={theme}
                />
              )}
              {activeTab === "support" && (
                <SupportTab
                  formData={formData}
                  onChange={handleFieldChange}
                  theme={theme}
                />
              )}
              {activeTab === "misc" && (
                <MiscTab
                  formData={formData}
                  onChange={handleFieldChange}
                  onToggle={handleCheckboxChange}
                  theme={theme}
                />
              )}
              {activeTab === "users" && (
                <UsersTab
                  users={users}
                  setUsers={setUsers}
                  theme={theme}
                  companyName={formData.subdomain}
                  role={userRole.role}
                  useFieldTicket={useFieldTicket}
                  jobTypes={jobTypes}
                />
              )}
              {activeTab === "leases" && (
                <LeasesTab
                  leases={leases}
                  setLeases={setLeases}
                  users={users}
                  purchasers={purchasers}
                  tags={tags}
                  theme={theme}
                  companyName={formData.subdomain}
                />
              )}
              {activeTab === "tags" && (
                <TagsTab
                  tags={tags}
                  setTags={setTags}
                  theme={theme}
                  companyName={formData.subdomain}
                />
              )}
              {activeTab === "logs" && (
                <React.Suspense
                  fallback={
                    <div className="flex h-64 items-center justify-center">
                      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500 border-solid"></div>
                    </div>
                  }
                >
                  <CompanyLogs
                    theme={theme}
                    company={formData.subdomain}
                    apiBase="https://house.ogendeavors.com/api"
                    pageSize={10000}
                    auditSince=""
                    auditSlim={false}
                  />
                </React.Suspense>
              )}
              {activeTab === "schedules" && (
                <ScheduledReportsPanel
                  subdomain={formData.subdomain}
                  users={users}
                  leases={leases}
                  tags={tags}
                  theme={theme}
                  baseUrl="https://house.ogendeavors.com/api"
                />
              )}
            </div>

            {/* Footer */}
            <div className="mt-4 flex justify-end space-x-2">
              <button
                onClick={closeForm}
                className={`px-4 py-2 rounded ${
                  theme === "dark"
                    ? "bg-gray-700 hover:bg-gray-600 text-gray-200"
                    : "bg-gray-300 hover:bg-gray-400 text-gray-800"
                }`}
              >
                Close
              </button>
              <button
                onClick={handleSubmitForm}
                className="px-4 py-2 rounded bg-blue-500 hover:bg-blue-600 text-white"
              >
                {formMode === "create" ? "Create" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** -------------------------------------
 *  Reusable Tab Button
 * ------------------------------------- */
function TabButton({ label, icon, active, onClick }) {
  return (
    <button
      className={`px-4 py-2 font-semibold rounded-t-lg transition-colors ${
        active
          ? "border-b-2 border-blue-500 text-blue-700"
          : "text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
      }`}
      onClick={onClick}
    >
      <FontAwesomeIcon icon={icon} className="mr-2" />
      {label}
    </button>
  );
}

/*──────────────────────────────────────────────────────────────
 * BasicInfoTab — regenerated 2025-07-31
 * • Checkbox state now honours truthy strings/numbers (“1”, "true", "Y")
 * • `normaliseClientPerms` coerces everything to real booleans
 *──────────────────────────────────────────────────────────────*/

/* ───────────── helpers ───────────── */
const DEFAULT_CLIENT_PERMS = {
  hasOgFiles: false,
  UseFieldTicket: false,
};

const toBool = (v) =>
  v === true ||
  v === 1 ||
  v === "1" ||
  v === "true" ||
  v === "TRUE" ||
  v === "Y" ||
  v === "y";

/** Accepts string | object | undefined, always returns proper boolean flags */
const normaliseClientPerms = (p) => {
  let obj;
  if (!p) obj = {};
  else if (typeof p === "string") {
    try {
      obj = JSON.parse(p);
    } catch {
      obj = {};
    }
  } else obj = { ...p };

  return {
    hasOgFiles: toBool(obj.hasOgFiles ?? DEFAULT_CLIENT_PERMS.hasOgFiles),
    UseFieldTicket: toBool(
      obj.UseFieldTicket ?? DEFAULT_CLIENT_PERMS.UseFieldTicket,
    ),
    hasOgCharts: toBool(obj.hasOgCharts ?? DEFAULT_CLIENT_PERMS.hasOgCharts),
  };
};

function BasicInfoTab({ formData, onChange, onToggle, theme, formMode }) {
  const isReadOnly = formMode === "edit"; // lock db creds on edit
  const perms = normaliseClientPerms(formData.perms);

  /* Update a single flag inside perms */
  const handlePermToggle = (key, checked) => {
    const next = { ...perms, [key]: checked };
    // Pass as *object*; parent can JSON-stringify before saving
    onChange({ target: { name: "perms", value: next } });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Subdomain */}
      <div>
        <label className="block mb-1 font-semibold">Subdomain</label>
        <input
          name="subdomain"
          value={formData.subdomain}
          onChange={onChange}
          readOnly={isReadOnly}
          className={getInputClasses(theme, { readOnly: isReadOnly })}
        />
      </div>

      {/* DB Name */}
      <div>
        <label className="block mb-1 font-semibold">DB Name</label>
        <input
          name="name"
          value={formData.name}
          onChange={onChange}
          readOnly={isReadOnly}
          className={getInputClasses(theme, { readOnly: isReadOnly })}
        />
      </div>

      {/* DB User */}
      <div>
        <label className="block mb-1 font-semibold">DB User</label>
        <input
          name="user"
          value={formData.user}
          onChange={onChange}
          readOnly={isReadOnly}
          className={getInputClasses(theme, { readOnly: isReadOnly })}
        />
      </div>

      {/* DB Password */}
      <div>
        <label className="block mb-1 font-semibold">DB Password</label>
        <input
          type="password"
          name="pwd"
          value={formData.pwd}
          onChange={onChange}
          readOnly={isReadOnly}
          className={getInputClasses(theme, { readOnly: isReadOnly })}
        />
      </div>

      {/* Company Name */}
      <div>
        <label className="block mb-1 font-semibold">Company Name</label>
        <input
          name="company"
          value={formData.company}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>

      {/* Type (O/P) */}
      <div>
        <label className="block mb-1 font-semibold">
          Type (O=Operator, P=Purchaser)
        </label>
        <select
          name="Type"
          value={formData.Type}
          onChange={onChange}
          className={getInputClasses(theme)}
        >
          <option value="O">Operator</option>
          <option value="P">Purchaser</option>
        </select>
      </div>

      {/* Active toggle */}
      <div className="flex items-center space-x-2 mt-2">
        <input
          id="activeToggle"
          type="checkbox"
          name="Active"
          checked={formData.Active === "Y"}
          onChange={onToggle}
        />
        <label htmlFor="activeToggle" className="font-semibold">
          Active?
        </label>
      </div>

      {/* Notes */}
      <div className="md:col-span-2">
        <label className="block mb-1 font-semibold">Notes</label>
        <textarea
          name="Notes"
          value={formData.Notes}
          onChange={onChange}
          rows={4}
          className={getInputClasses(theme)}
        />
      </div>

      {/* ───── Client-level Permissions ───── */}
      <div className="md:col-span-2 border-t pt-2 mt-2">
        <h5 className="text-sm font-bold mb-1">Permissions</h5>

        {/* hasOgFiles */}
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold">OgFiles enabled</span>
          <label className="flex items-center text-xs">
            <input
              type="checkbox"
              checked={perms.hasOgFiles}
              onChange={(e) => handlePermToggle("hasOgFiles", e.target.checked)}
              className="mr-1"
            />
            Allow
          </label>
        </div>

        {/* hasOgFiles */}
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold">OgCharts enabled</span>
          <label className="flex items-center text-xs">
            <input
              type="checkbox"
              checked={perms.hasOgCharts}
              onChange={(e) =>
                handlePermToggle("hasOgCharts", e.target.checked)
              }
              className="mr-1"
            />
            Allow
          </label>
        </div>

        {/* UseFieldTicket */}
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold">Use FieldTicket</span>
          <label className="flex items-center text-xs">
            <input
              type="checkbox"
              checked={perms.UseFieldTicket}
              onChange={(e) =>
                handlePermToggle("UseFieldTicket", e.target.checked)
              }
              className="mr-1"
            />
            Enable
          </label>
        </div>
      </div>
    </div>
  );
}

/** -------------------------------------
 *  FEATURES TAB
 * ------------------------------------- */
function FeaturesTab({ formData, onToggle, onChange, theme }) {
  const toggles = [
    { name: "useBSW", label: "Use BSW?" },
    { name: "use24", label: "Use 24-Hour Format?" },
    { name: "useMaps", label: "Use Maps?" },
    { name: "useLinePressure", label: "Use Line Pressure?" },
    { name: "ShowWells", label: "Show Wells?" },
    { name: "UseWater", label: "Use Water?" },
    { name: "UseGas", label: "Use Gas?" },
    { name: "ShowWellPropertyNum", label: "Show Well Property Num?" },
    { name: "UseWaterTanks", label: "Use Water Tanks?" },
    { name: "ShowZeros", label: "Show Zero Gauges?" },
    { name: "UseColorCut", label: "Use Color Cut?" },
    { name: "GaugeImportFile", label: "Gauge Import File?" },
    { name: "NetInterestBbls", label: "Net Interest Bbls?" },
    { name: "PR", label: "Use PR?" },
    { name: "ShowPOC", label: "Show POC?" },
    { name: "ShowLoads", label: "Show Loads?" },
    { name: "Data", label: "Use Data?" },
    { name: "RequireWellReason", label: "Require Well Reason?" },
    { name: "UseImages", label: "Use Images?" },
    { name: "UseFieldTicket", label: "Use Field Ticket?" },
    { name: "UseBradenheadPressure", label: "Use Bradenhead Pressure?" },
    { name: "AlwaysOverrideRT", label: "Always Override RT?" },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {toggles.map((t) => (
        <div key={t.name} className="flex items-center space-x-2">
          <input
            id={t.name}
            type="checkbox"
            name={t.name}
            checked={formData[t.name] === "Y"}
            onChange={onToggle}
          />
          <label htmlFor={t.name} className="font-semibold">
            {t.label}
          </label>
        </div>
      ))}
      {/* PumperEditDaysBack */}
      <div>
        <label className="block mb-1 font-semibold">
          Pumper Edit Days Back
        </label>
        <input
          type="number"
          name="PumperEditDaysBack"
          value={formData.PumperEditDaysBack}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
    </div>
  );
}

/** -------------------------------------
 *  BILLING TAB
 * ------------------------------------- */
function BillingTab({ formData, onChange, onToggle, theme }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Bill */}
      <div className="flex items-center space-x-2">
        <input
          id="billToggle"
          type="checkbox"
          name="Bill"
          checked={formData.Bill === "Y"}
          onChange={onToggle}
        />
        <label htmlFor="billToggle" className="font-semibold">
          Bill?
        </label>
      </div>
      {/* BillQuarterly */}
      <div className="flex items-center space-x-2">
        <input
          id="billQuarterly"
          type="checkbox"
          name="BillQuarterly"
          checked={formData.BillQuarterly === "Y"}
          onChange={onToggle}
        />
        <label htmlFor="billQuarterly" className="font-semibold">
          Bill Quarterly?
        </label>
      </div>
      {/* Rate1 */}
      <div>
        <label className="block mb-1 font-semibold">Rate1</label>
        <input
          type="number"
          step="0.01"
          name="Rate1"
          value={formData.Rate1}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      {/* Rate2 */}
      <div>
        <label className="block mb-1 font-semibold">Rate2</label>
        <input
          type="number"
          step="0.01"
          name="Rate2"
          value={formData.Rate2}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      {/* Rate3 */}
      <div>
        <label className="block mb-1 font-semibold">Rate3</label>
        <input
          type="number"
          step="0.01"
          name="Rate3"
          value={formData.Rate3}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
    </div>
  );
}

/** -------------------------------------
 *  LAYOUT TAB
 * ------------------------------------- */
function LayoutTab({ formData, onChange, theme }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block mb-1 font-semibold">App Name</label>
        <input
          name="AppName"
          value={formData.AppName}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">App Logo URL</label>
        <input
          name="AppLogo"
          value={formData.AppLogo}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">App Author</label>
        <input
          name="AppAuthor"
          value={formData.AppAuthor}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">Current Layout</label>
        <select
          name="CurrentLayout"
          value={formData.CurrentLayout}
          onChange={onChange}
          className={getInputClasses(theme)}
        >
          <option value="O">Original</option>
          <option value="A">Oct2023</option>
        </select>
      </div>
      <div>
        <label className="block mb-1 font-semibold">Color Cut Label</label>
        <input
          name="ColorCutLabel"
          value={formData.ColorCutLabel}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">Current Custom Col</label>
        <input
          name="CurrentCustomCol"
          value={formData.CurrentCustomCol}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">Current Custom Col2</label>
        <input
          name="CurrentCustomCol2"
          value={formData.CurrentCustomCol2}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">Inj Custom Col1</label>
        <input
          name="InjCustomCol1"
          value={formData.InjCustomCol1}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">Inj Custom Col2</label>
        <input
          name="InjCustomCol2"
          value={formData.InjCustomCol2}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
    </div>
  );
}

/** -------------------------------------
 *  SUPPORT TAB
 * ------------------------------------- */
function SupportTab({ formData, onChange, theme }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block mb-1 font-semibold">Support Email</label>
        <input
          name="SupportEmail"
          value={formData.SupportEmail}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">Support Phone</label>
        <input
          name="SupportPhone"
          value={formData.SupportPhone}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div className="md:col-span-2">
        <label className="block mb-1 font-semibold">Support Address</label>
        <input
          name="SupportAddress"
          value={formData.SupportAddress}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
    </div>
  );
}

/** -------------------------------------
 *  MISC TAB
 * ------------------------------------- */
function MiscTab({ formData, onChange, onToggle, theme }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block mb-1 font-semibold">Operator Num</label>
        <input
          name="OperatorNum"
          value={formData.OperatorNum}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>

      <div>
        <label className="block mb-1 font-semibold">PR Start Day</label>
        <input
          type="number"
          name="PRStartDay"
          value={formData.PRStartDay}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>

      <div>
        <label className="block mb-1 font-semibold">Manual Water Label</label>
        <input
          name="ManualWaterLabel"
          value={formData.ManualWaterLabel}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>

      <div>
        <label className="block mb-1 font-semibold">Manual Water Label 2</label>
        <input
          name="ManualWaterLabel2"
          value={formData.ManualWaterLabel2}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>

      <div>
        <label className="block mb-1 font-semibold">Line Height Inches</label>
        <input
          type="number"
          step="0.01"
          min="0"
          name="lineheightinches"
          value={formData.lineheightinches ?? ""}
          onChange={onChange}
          className={getInputClasses(theme)}
          placeholder="0.00"
        />
      </div>

      <div className="md:col-span-2">
        <label className="block mb-1 font-semibold">Notify Emails</label>
        <textarea
          name="NotifyEmails"
          rows={3}
          value={formData.NotifyEmails}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
    </div>
  );
}
/** -------------------------------------
 *  LEASES TAB
 * ------------------------------------- */

function LeasesTab({
  leases,
  setLeases,
  users,
  purchasers, // Passed in from props
  tags, // Passed in from props
  theme,
  companyName,
  subdomain,
}) {
  const [showLeaseModal, setShowLeaseModal] = useState(false);
  const [editingLease, setEditingLease] = useState(null);
  const [activeTab, setActiveTab] = useState("basic");
  const [isEditMode, setIsEditMode] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: "" });

  // For searching and sorting (leases grid)
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("LeaseID");
  const [sortDirection, setSortDirection] = useState("asc");

  // Show/Hide inactive leases (default Hide)
  const [showInactive, setShowInactive] = useState(false);

  // Replace Pumper modal
  const [showReplacePumperModal, setShowReplacePumperModal] = useState(false);
  const [replaceFromPumper, setReplaceFromPumper] = useState("");
  const [replaceToPumper, setReplaceToPumper] = useState("");
  const [replaceBusy, setReplaceBusy] = useState(false);

  // Valid pumpers
  const [validPumpers, setValidPumpers] = useState([]);

  // Wells grid state (in modal)
  const [wellSearch, setWellSearch] = useState("");
  const [wellSortBy, setWellSortBy] = useState("WellID");
  const [wellSortDirection, setWellSortDirection] = useState("asc");
  const [showInactiveWells, setShowInactiveWells] = useState(false);

  // Tanks grid state (in modal)
  const [tankSearch, setTankSearch] = useState("");
  const [tankSortBy, setTankSortBy] = useState("TankID");
  const [tankSortDirection, setTankSortDirection] = useState("asc");
  const [showInactiveTanks, setShowInactiveTanks] = useState(false);

  const isDark = theme === "dark";

  // -------------------------------
  // HELPER STYLES
  // -------------------------------
  const getInputClasses = (isDarkMode) =>
    isDarkMode
      ? "w-full px-2 py-1 bg-gray-700 text-gray-100 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
      : "w-full px-2 py-1 bg-white text-gray-800 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500";

  const getHeaderClasses = () =>
    `border-b ${
      isDark
        ? "bg-gray-700 text-gray-100 border-gray-600"
        : "bg-gray-100 text-gray-800 border-gray-200"
    }`;

  const getRowClasses = () =>
    `border-b ${
      isDark ? "border-gray-600 hover:bg-gray-600" : "hover:bg-gray-50"
    }`;

  // Helper to compute valid pumpers from full users list
  const computeValidPumpers = (allUsers = []) =>
    allUsers.filter((u) => {
      if (!u) return false;
      if (u.Disabled === "1") return false;

      const role = (u.Role || "").toUpperCase();
      // Exclude Investor and Read-only roles
      if (role === "I" || role === "R") return false;

      if (!u.perms) return false;

      let permsObj;
      try {
        permsObj = JSON.parse(u.perms);
      } catch (e) {
        return false;
      }

      const ogpumperRead = permsObj?.ogpumper?.read === true;
      const ogfieldticketRead = permsObj?.ogfieldticket?.read === true;

      // Must have read perms in at least one of these apps
      return ogpumperRead || ogfieldticketRead;
    });

  // Keep validPumpers in sync with users prop
  useEffect(() => {
    setValidPumpers(computeValidPumpers(users || []));
  }, [users]);

  // Reconcile active tab when WellType changes (INJ hides Tanks & Purchasers, shows Injection)
  useEffect(() => {
    if (!editingLease) return;
    const isInjection = (editingLease.WellType || "").toUpperCase() === "INJ";
    if (isInjection) {
      if (activeTab === "tanks" || activeTab === "purchasers") {
        setActiveTab("injection");
      }
    } else {
      if (activeTab === "injection") setActiveTab("basic");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingLease?.WellType]);

  // Blank lease object for "Add New"
  const blankLease = {
    LeaseID: "",
    LeaseName: "",
    GroupID: "",
    FieldName: "",
    County: "",
    PumperID: "",
    PumperRoute: "",
    ReliefID: "",
    District: "",
    RRC: "",
    WellType: "OIL",
    ShowOil: "Y",
    ShowWater: "Y",
    ShowGas: "Y",
    WaterMeter: "N",
    Active: "Y",

    // Purchasers
    Purchaser: "",
    PurchaserLeaseNo: "",
    WaterTransport: "",

    // Injection
    UIC: "",
    Target: 0, // Daily Target (int)
    MaxInj: 0, // int
    MaxPressure: 0, // int

    PropertyNum: "",
    CP: "",
    Tag1: "",
    Tag2: "",
    Tag3: "",
    Tag4: "",
    Investor1: "",
    Investor2: "",
    wells: [],
    tanks: [],
  };

  // -------------------------------
  // CREATE/EDIT/COPY LEASE
  // -------------------------------
  const handleCreateLease = () => {
    setEditingLease({ ...blankLease });
    setIsEditMode(false);
    setActiveTab("basic");
    setShowLeaseModal(true);

    // reset sub-grid state
    setWellSearch("");
    setTankSearch("");
  };

  const handleExportLeases = () => {
    try {
      if (!leases?.length) throw new Error("No leases to export");

      /* ───────────── helper ───────────── */
      const serialise = (val) => {
        if (Array.isArray(val)) {
          return val
            .map((el) => {
              if (el == null) return "";
              if (typeof el === "object")
                return el.name ?? el.id ?? JSON.stringify(el);
              return String(el);
            })
            .join("; ");
        }
        if (val && typeof val === "object") return JSON.stringify(val);
        return val ?? "";
      };

      /* ───────────── build CSV ───────────── */
      const headers = Object.keys(leases[0]);
      const csv = [
        headers.join(","),
        ...leases.map((lease) =>
          headers
            .map((h) => {
              const escaped = String(serialise(lease[h])).replace(/"/g, '""');
              return `"${escaped}"`;
            })
            .join(","),
        ),
      ].join("\n");

      /* ───────────── download ───────────── */
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `leases_${companyName}_${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed", err);
      alert("Failed to export leases. Check console for details.");
    }
  };

  const handleEditLease = (lease) => {
    const clone = JSON.parse(JSON.stringify(lease));

    if (!Array.isArray(clone.wells)) clone.wells = [];
    if (!Array.isArray(clone.tanks)) clone.tanks = [];

    // Ensure newer fields exist for older records
    if (typeof clone.UIC !== "string") clone.UIC = "";
    if (typeof clone.County !== "string") clone.County = "";
    if (typeof clone.PurchaserLeaseNo !== "string") clone.PurchaserLeaseNo = "";
    if (typeof clone.WaterTransport !== "string") clone.WaterTransport = "";

    // Ensure newer well fields exist for older records
    clone.wells = clone.wells.map((well) => ({
      ...well,
      ElectricMeter:
        typeof well?.ElectricMeter === "string" ? well.ElectricMeter : "",
    }));

    // ints (default 0)
    if (
      clone.Target === null ||
      clone.Target === undefined ||
      clone.Target === ""
    )
      clone.Target = 0;
    if (
      clone.MaxInj === null ||
      clone.MaxInj === undefined ||
      clone.MaxInj === ""
    )
      clone.MaxInj = 0;
    if (
      clone.MaxPressure === null ||
      clone.MaxPressure === undefined ||
      clone.MaxPressure === ""
    )
      clone.MaxPressure = 0;

    setEditingLease(clone);
    setIsEditMode(true);
    setActiveTab("basic");
    setShowLeaseModal(true);

    // reset sub-grid state
    setWellSearch("");
    setTankSearch("");
  };

  // Copy a lease minus wells/tanks arrays
  const handleCopyLease = (lease) => {
    const clone = JSON.parse(JSON.stringify(lease));
    delete clone.wells;
    delete clone.tanks;

    clone.LeaseID = `${clone.LeaseID}-COPY`;

    // Ensure new fields exist
    if (typeof clone.UIC !== "string") clone.UIC = "";
    if (typeof clone.WaterTransport !== "string") clone.WaterTransport = "";
    if (
      clone.Target === null ||
      clone.Target === undefined ||
      clone.Target === ""
    )
      clone.Target = 0;
    if (
      clone.MaxInj === null ||
      clone.MaxInj === undefined ||
      clone.MaxInj === ""
    )
      clone.MaxInj = 0;
    if (
      clone.MaxPressure === null ||
      clone.MaxPressure === undefined ||
      clone.MaxPressure === ""
    )
      clone.MaxPressure = 0;

    setEditingLease(clone);
    setIsEditMode(false);
    setActiveTab("basic");
    setShowLeaseModal(true);

    setWellSearch("");
    setTankSearch("");
  };

  const handleCancelLease = () => {
    setEditingLease(null);
    setShowLeaseModal(false);
  };

  // -------------------------------
  // SAVE HELPERS
  // -------------------------------
  const normalizeLeaseForSave = (lease, allUsers) => {
    const usersArr = allUsers || [];
    const allUserIds = new Set(usersArr.map((u) => u.UserID));
    const validPumperIds = new Set(
      computeValidPumpers(usersArr).map((u) => u.UserID),
    );

    const out = { ...lease };

    // ----- FK fields -----
    if (!out.PumperID || !validPumperIds.has(out.PumperID)) {
      throw new Error("Invalid PumperID");
    }

    ["ReliefID", "Investor1", "Investor2"].forEach((k) => {
      if (out[k] === "") out[k] = null;
      else if (out[k] && !allUserIds.has(out[k])) out[k] = null;
    });

    // ----- Y/N flags -> 'Y'/'N'
    [
      "ShowOil",
      "ShowWater",
      "ShowGas",
      "ShowLACT",
      "ShowTbg",
      "ShowCsg",
      "ShowStatic",
      "ShowDiff",
      "ShowFlowRate",
      "ShowChoke",
      "ShowMcfAccum",
      "ShowHoursOn",
      "GasLift",
      "GasMeter",
      "WaterMeter",
      "Active",
      "ExcludeFromPR",
      "PumpOnMeter",
    ].forEach((k) => {
      if (k in out) {
        const v = out[k];
        out[k] = v === "Y" ? "Y" : v === "N" ? "N" : v ? "Y" : "N";
      }
    });

    // ----- Trim to schema widths
    const trim = (s, n) => (typeof s === "string" ? s.slice(0, n) : s);
    out.LeaseName = trim(out.LeaseName, 35);
    out.GroupID = trim(out.GroupID, 20);
    out.Tag1 = out.Tag1 === "" ? null : trim(out.Tag1, 20);
    out.Tag2 = out.Tag2 === "" ? null : trim(out.Tag2, 20);
    out.Tag3 = out.Tag3 === "" ? null : trim(out.Tag3, 20);
    out.Tag4 = out.Tag4 === "" ? null : trim(out.Tag4, 20);
    out.RRC = trim(out.RRC, 6);
    out.API = trim(out.API, 30);
    out.FieldName = trim(out.FieldName, 100);
    out.Legal = trim(out.Legal, 255);
    out.PropertyNum = trim(out.PropertyNum, 10);
    out.CP = trim(out.CP, 10);
    out.UIC = trim(out.UIC, 9);
    out.County = trim(out.County, 50);
    out.PurchaserLeaseNo = trim(out.PurchaserLeaseNo, 50);
    out.WaterTransport = trim(out.WaterTransport, 50);

    // ----- Numerics
    ["PumperRoute", "ReportOrder"].forEach((k) => {
      if (out[k] === "" || out[k] === null) delete out[k];
      else if (out[k] !== undefined) out[k] = parseInt(out[k], 10);
    });

    // ints (keep int)
    ["MaxInj", "MaxPressure", "Target"].forEach((k) => {
      if (out[k] === "" || out[k] === null) delete out[k];
      else if (out[k] !== undefined) out[k] = parseInt(out[k], 10);
    });

    ["WIDecimal", "NetLeaseDecimal"].forEach((k) => {
      if (out[k] === "" || out[k] === null) delete out[k];
    });

    ["lat", "lon"].forEach((k) => {
      if (out[k] === "" || out[k] === null || isNaN(+out[k])) delete out[k];
      else out[k] = String(out[k]);
    });

    return out;
  };

  const handleSaveLease = async () => {
    if (!editingLease) return;

    if (!editingLease.LeaseID || !editingLease.LeaseID.trim()) {
      alert("A LeaseID is required.");
      return;
    }
    if (!editingLease.LeaseName || !editingLease.LeaseName.trim()) {
      alert("A LeaseName is required.");
      return;
    }

    const existsLocally = leases.some(
      (l) => l.LeaseID === editingLease.LeaseID,
    );
    if (!isEditMode && existsLocally) {
      alert(
        `LeaseID "${editingLease.LeaseID}" already exists.  Pick a unique ID.`,
      );
      return;
    }

    try {
      const payload = normalizeLeaseForSave(editingLease, users);
      const isInjection =
        (payload.WellType || editingLease.WellType || "").toUpperCase() ===
        "INJ";

      // Always include wells if present
      if (Array.isArray(editingLease.wells) && editingLease.wells.length > 0) {
        payload.wells = editingLease.wells.map((w) => ({
          ...w,
          LeaseID: editingLease.LeaseID,
          ElectricMeter:
            typeof w?.ElectricMeter === "string"
              ? w.ElectricMeter.slice(0, 50)
              : "",
        }));
      }

      // Only include tanks for NON-INJ
      if (
        !isInjection &&
        Array.isArray(editingLease.tanks) &&
        editingLease.tanks.length > 0
      ) {
        payload.tanks = editingLease.tanks.map((t) => ({
          ...t,
          LeaseID: editingLease.LeaseID,
        }));
      }

      const isExisting = leases.some((l) => l.LeaseID === editingLease.LeaseID);
      let url, method;

      if (!isExisting) {
        method = "POST";
        url = `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
          companyName,
        )}&createLease=1`;
      } else {
        method = "PATCH";
        url = `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
          companyName,
        )}&lid=${encodeURIComponent(editingLease.LeaseID)}`;
      }

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const msg = await response.text();
        throw new Error(
          `Lease save failed. Status: ${response.status}. ${msg}`,
        );
      }

      // Update local state to mirror what was sent/saved
      let updatedLeases;
      if (!isExisting) {
        updatedLeases = [...leases, { ...editingLease, ...payload }];
      } else {
        updatedLeases = leases.map((l) =>
          l.LeaseID === editingLease.LeaseID ? { ...l, ...payload } : l,
        );
      }
      setLeases(updatedLeases);

      setShowLeaseModal(false);
    } catch (err) {
      console.error("Error saving lease:", err);
      alert(err.message || "Error saving lease. Check console for details.");
    }
  };

  // -------------------------------
  // WELLS / TANKS - ADD / REMOVE
  // -------------------------------
  const handleRemoveWell = async (well) => {
    if (!well.UniqID) {
      setEditingLease((prev) => ({
        ...prev,
        wells: (prev.wells || []).filter((w) => w !== well),
      }));
      return;
    }

    const confirmed = window.confirm(
      `Remove well "${well.WellID}" from server?`,
    );
    if (!confirmed) return;

    try {
      const url = `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
        companyName,
      )}&lid=${encodeURIComponent(
        editingLease.LeaseID,
      )}&wid=${encodeURIComponent(well.UniqID)}`;

      const resp = await fetch(url, { method: "DELETE" });
      if (!resp.ok) {
        const msg = await resp.text();
        throw new Error(`Delete well failed: ${resp.status} - ${msg}`);
      }

      setEditingLease((prev) => ({
        ...prev,
        wells: (prev.wells || []).filter((w) => w.UniqID !== well.UniqID),
      }));

      setLeases((prevLeases) =>
        prevLeases.map((lease) => {
          if (lease.LeaseID === editingLease.LeaseID) {
            return {
              ...lease,
              wells: (lease.wells || []).filter(
                (w) => w.UniqID !== well.UniqID,
              ),
            };
          }
          return lease;
        }),
      );
    } catch (err) {
      console.error("Error removing well:", err);
      setToast({
        visible: true,
        message:
          "Well has historical data and cannot be removed.  Please set Active to No to disable.",
      });
    }
  };

  const handleRemoveTank = async (tank) => {
    if (!tank.UniqID) {
      setEditingLease((prev) => ({
        ...prev,
        tanks: (prev.tanks || []).filter((t) => t !== tank),
      }));
      return;
    }

    const confirmed = window.confirm(
      `Remove tank "${tank.TankID}" from server? This cannot be undone.`,
    );
    if (!confirmed) return;

    try {
      const url = `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
        companyName,
      )}&lid=${encodeURIComponent(
        editingLease.LeaseID,
      )}&tid=${encodeURIComponent(tank.UniqID)}`;

      const resp = await fetch(url, { method: "DELETE" });
      if (!resp.ok) {
        const msg = await resp.text();
        throw new Error(`Delete tank failed: ${resp.status} - ${msg}`);
      }

      setEditingLease((prev) => ({
        ...prev,
        tanks: (prev.tanks || []).filter((t) => t.UniqID !== tank.UniqID),
      }));

      setLeases((prevLeases) =>
        prevLeases.map((lease) => {
          if (lease.LeaseID === editingLease.LeaseID) {
            return {
              ...lease,
              tanks: (lease.tanks || []).filter(
                (t) => t.UniqID !== tank.UniqID,
              ),
            };
          }
          return lease;
        }),
      );
    } catch (err) {
      console.error("Error removing tank:", err);
      setToast({
        visible: true,
        message:
          "Tank has historical data and cannot be removed.  Please set Active to No to disable.",
      });
    }
  };

  const handleAddWell = () => {
    setEditingLease((prev) => {
      const newWell = {
        UniqID: "",
        LeaseID: prev.LeaseID,
        WellID: "",
        Active: "Y",
        PropertyNum: "",
        AllocPct: "0.0000",
        RRCLeaseID: "",
        CP: "",
        ElectricMeter: "",
      };
      return { ...prev, wells: [newWell, ...(prev.wells || [])] };
    });
  };

  const handleAddTank = () => {
    setEditingLease((prev) => {
      const newTank = {
        UniqID: "",
        LeaseID: prev.LeaseID,
        TankID: "",
        Size: 0,
        BBLSperInch: "0.000",
        Active: "Y",
        TankType: "T",
        GasCoeff: "0.00",
        ExcludeDrawsFromProd: "N",
        WPTankNum: "",
      };
      return { ...prev, tanks: [newTank, ...(prev.tanks || [])] };
    });
  };

  // -------------------------------
  // FIELD HANDLERS
  // -------------------------------
  const handleLeaseFieldChange = (e) => {
    const { name, value } = e.target;
    setEditingLease((prev) => ({ ...prev, [name]: value }));
  };

  const handleLeaseCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setEditingLease((prev) => ({ ...prev, [name]: checked ? "Y" : "N" }));
  };

  const handleWellChange = (index, field, value) => {
    setEditingLease((prev) => {
      const newWells = [...(prev.wells || [])];
      newWells[index] = { ...newWells[index], [field]: value };
      return { ...prev, wells: newWells };
    });
  };

  const handleTankChange = (index, field, value) => {
    setEditingLease((prev) => {
      const newTanks = [...(prev.tanks || [])];
      newTanks[index] = { ...newTanks[index], [field]: value };
      return { ...prev, tanks: newTanks };
    });
  };

  // -------------------------------
  // SEARCH & SORT (leases)
  // -------------------------------
  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const handleSort = (columnKey) => {
    if (sortBy === columnKey)
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    else {
      setSortBy(columnKey);
      setSortDirection("asc");
    }
  };

  const sortedAndFilteredLeases = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();
    const filtered = (leases || []).filter((lease) => {
      const activePass = showInactive || lease.Active === "Y";

      const matchesSearch =
        (lease.LeaseID || "").toLowerCase().includes(lowerSearch) ||
        (lease.LeaseName || "").toLowerCase().includes(lowerSearch) ||
        (lease.RRC || "").toLowerCase().includes(lowerSearch) ||
        (lease.WellType || "").toLowerCase().includes(lowerSearch) ||
        (lease.PumperID || "").toLowerCase().includes(lowerSearch) ||
        (lease.ReliefID || "").toLowerCase().includes(lowerSearch) ||
        (lease.CompanyName || "").toLowerCase().includes(lowerSearch) ||
        (lease.Subdomain || "").toLowerCase().includes(lowerSearch) ||
        String(lease.PumperRoute ?? "")
          .toLowerCase()
          .includes(lowerSearch);

      return activePass && matchesSearch;
    });

    return filtered.sort((a, b) => {
      const valA = a[sortBy] ?? "";
      const valB = b[sortBy] ?? "";

      if (typeof valA === "string" && typeof valB === "string") {
        const comparison = valA.localeCompare(valB);
        return sortDirection === "asc" ? comparison : -comparison;
      } else {
        if (valA < valB) return sortDirection === "asc" ? -1 : 1;
        if (valA > valB) return sortDirection === "asc" ? 1 : -1;
        return 0;
      }
    });
  }, [leases, searchTerm, sortBy, sortDirection, showInactive]);

  // -------------------------------
  // SEARCH & SORT (wells)
  // -------------------------------
  const handleWellSort = (columnKey) => {
    if (wellSortBy === columnKey)
      setWellSortDirection(wellSortDirection === "asc" ? "desc" : "asc");
    else {
      setWellSortBy(columnKey);
      setWellSortDirection("asc");
    }
  };

  const displayedWells = useMemo(() => {
    const arr = Array.isArray(editingLease?.wells) ? editingLease.wells : [];
    const lower = (wellSearch || "").toLowerCase();

    let rows = arr.map((well, idx) => ({ well, idx }));

    rows = rows.filter(({ well }) => {
      const activePass = showInactiveWells || (well.Active || "Y") === "Y";
      const matches =
        String(well.WellID || "")
          .toLowerCase()
          .includes(lower) ||
        String(well.PropertyNum || "")
          .toLowerCase()
          .includes(lower) ||
        String(well.RRCLeaseID || "")
          .toLowerCase()
          .includes(lower) ||
        String(well.CP || "")
          .toLowerCase()
          .includes(lower) ||
        String(well.ElectricMeter || "")
          .toLowerCase()
          .includes(lower);
      return activePass && matches;
    });

    rows.sort((a, b) => {
      const va = a.well?.[wellSortBy] ?? "";
      const vb = b.well?.[wellSortBy] ?? "";
      const sa = String(va);
      const sb = String(vb);
      const cmp = sa.localeCompare(sb);
      return wellSortDirection === "asc" ? cmp : -cmp;
    });

    return rows;
  }, [
    editingLease?.wells,
    wellSearch,
    wellSortBy,
    wellSortDirection,
    showInactiveWells,
  ]);

  // -------------------------------
  // SEARCH & SORT (tanks)
  // -------------------------------
  const handleTankSort = (columnKey) => {
    if (tankSortBy === columnKey)
      setTankSortDirection(tankSortDirection === "asc" ? "desc" : "asc");
    else {
      setTankSortBy(columnKey);
      setTankSortDirection("asc");
    }
  };

  const displayedTanks = useMemo(() => {
    const arr = Array.isArray(editingLease?.tanks) ? editingLease.tanks : [];
    const lower = (tankSearch || "").toLowerCase();

    let rows = arr.map((tank, idx) => ({ tank, idx }));

    rows = rows.filter(({ tank }) => {
      const activePass = showInactiveTanks || (tank.Active || "Y") === "Y";
      const matches =
        String(tank.TankID || "")
          .toLowerCase()
          .includes(lower) ||
        String(tank.TankType || "")
          .toLowerCase()
          .includes(lower) ||
        String(tank.WPTankNum || "")
          .toLowerCase()
          .includes(lower);
      return activePass && matches;
    });

    rows.sort((a, b) => {
      const va = a.tank?.[tankSortBy] ?? "";
      const vb = b.tank?.[tankSortBy] ?? "";
      const sa = String(va);
      const sb = String(vb);
      const cmp = sa.localeCompare(sb);
      return tankSortDirection === "asc" ? cmp : -cmp;
    });

    return rows;
  }, [
    editingLease?.tanks,
    tankSearch,
    tankSortBy,
    tankSortDirection,
    showInactiveTanks,
  ]);

  // -------------------------------
  // OPTIONS
  // -------------------------------
  const pumperOptions = validPumpers;
  const reliefOptions = validPumpers;
  const investorOptions = (users || []).filter((user) => user.Role === "I");

  // -------------------------------
  // Replace Pumper actions
  // -------------------------------
  const openReplacePumper = () => {
    setReplaceFromPumper("");
    setReplaceToPumper("");
    setShowReplacePumperModal(true);
  };

  const closeReplacePumper = () => {
    if (!replaceBusy) setShowReplacePumperModal(false);
  };

  const confirmReplacePumper = async () => {
    if (!replaceFromPumper || !replaceToPumper) {
      alert("Please choose both Current Pumper and New Pumper.");
      return;
    }
    if (replaceFromPumper === replaceToPumper) {
      alert("Current Pumper and New Pumper cannot be the same.");
      return;
    }

    const fromUser = (validPumpers || []).find(
      (u) => u.UserID === replaceFromPumper,
    );
    const toUser = (validPumpers || []).find(
      (u) => u.UserID === replaceToPumper,
    );
    const fromName = fromUser
      ? `${fromUser.UserID} - ${fromUser.FullName}`
      : replaceFromPumper;
    const toName = toUser
      ? `${toUser.UserID} - ${toUser.FullName}`
      : replaceToPumper;
    const localCount = (leases || []).filter(
      (l) => l.Active === "Y" && l.PumperID === replaceFromPumper,
    ).length;

    const sure = window.confirm(
      `Are you sure you want to move all ACTIVE leases from:\n\n` +
        `• ${fromName}\n` +
        `to\n` +
        `• ${toName}\n\n` +
        `About ${localCount} lease(s) appear to be affected locally. This cannot be undone.`,
    );
    if (!sure) return;

    try {
      setReplaceBusy(true);
      const url = `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
        companyName,
      )}`;
      const resp = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          replacePumper: true,
          currentPumper: replaceFromPumper,
          newPumper: replaceToPumper,
        }),
      });
      if (!resp.ok) {
        const msg = await resp.text();
        throw new Error(`Replace pumper failed: ${resp.status}. ${msg}`);
      }
      const data = await resp.json();

      setLeases((prev) =>
        prev.map((l) =>
          l.Active === "Y" && l.PumperID === replaceFromPumper
            ? { ...l, PumperID: replaceToPumper }
            : l,
        ),
      );

      alert(
        `Success. Moved all ACTIVE leases from ${replaceFromPumper} to ${replaceToPumper}.` +
          (typeof data.affectedLeases !== "undefined"
            ? ` Updated ${data.affectedLeases} lease(s).`
            : ""),
      );
      setShowReplacePumperModal(false);
    } catch (e) {
      console.error(e);
      alert("Failed to replace pumper. See console for details.");
    } finally {
      setReplaceBusy(false);
    }
  };

  // -------------------------------
  // RENDER
  // -------------------------------
  return (
    <div className="p-2">
      {/* Top bar: search & actions */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 mb-3">
        <div className="flex-1 flex items-center gap-3">
          <div className="relative">
            <FontAwesomeIcon
              icon={faSearch}
              className={`absolute left-3 top-2.5 ${
                isDark ? "text-gray-400" : "text-gray-500"
              }`}
            />
            <input
              type="text"
              placeholder="Search leases..."
              value={searchTerm}
              onChange={handleSearchChange}
              className={`pl-9 pr-4 py-1 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark
                  ? "bg-gray-700 text-gray-100 border border-gray-600"
                  : "bg-white text-gray-700 border border-gray-300"
              }`}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={openReplacePumper}
            className="inline-flex items-center px-3 py-1 rounded bg-amber-500 hover:bg-amber-600 text-white"
            title="Move all ACTIVE leases from one pumper to another"
          >
            Replace Pumper
          </button>

          <button
            onClick={handleCreateLease}
            className="inline-flex items-center px-3 py-1 rounded bg-blue-500 hover:bg-blue-600 text-white"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-1" />
            New
          </button>

          <button
            onClick={handleExportLeases}
            className="inline-flex items-center px-3 py-1 rounded bg-teal-500 hover:bg-teal-600 text-white"
          >
            <FontAwesomeIcon icon={faFileExport} className="mr-1" />
            Export
          </button>

          <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none ml-2">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            <span className={isDark ? "text-gray-200" : "text-gray-700"}>
              Show Inactive
            </span>
          </label>
        </div>
      </div>

      {/* Leases Table */}
      {sortedAndFilteredLeases && sortedAndFilteredLeases.length > 0 ? (
        <div className="overflow-x-auto rounded-md shadow-sm">
          <table className="min-w-full text-sm">
            <thead className={getHeaderClasses()}>
              <tr>
                <SortableHeader
                  label="LeaseID"
                  columnKey="LeaseID"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="LeaseName"
                  columnKey="LeaseName"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="RRC #"
                  columnKey="RRC"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="WellType"
                  columnKey="WellType"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="Pumper"
                  columnKey="PumperID"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="Route"
                  columnKey="PumperRoute"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="Relief"
                  columnKey="ReliefID"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="Active"
                  columnKey="Active"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <th className="py-2 px-4 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedAndFilteredLeases.map((lease, idx) => (
                <tr key={idx} className={getRowClasses()}>
                  <td className="py-2 px-4">{lease.LeaseID}</td>
                  <td className="py-2 px-4">{lease.LeaseName}</td>
                  <td className="py-2 px-4">{lease.RRC}</td>
                  <td className="py-2 px-4">{lease.WellType}</td>
                  <td className="py-2 px-4">{lease.PumperID || ""}</td>
                  <td className="py-2 px-4">{lease.PumperRoute ?? ""}</td>
                  <td className="py-2 px-4">{lease.ReliefID || ""}</td>
                  <td className="py-2 px-4 text-right">
                    {lease.Active === "Y" ? <span title="Active">✓</span> : ""}
                  </td>
                  <td className="py-2 px-4">
                    <button
                      onClick={() => handleEditLease(lease)}
                      className="inline-flex items-center px-2 py-1 rounded bg-green-500 hover:bg-green-600 text-white"
                    >
                      <FontAwesomeIcon icon={faEdit} className="mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleCopyLease(lease)}
                      className="inline-flex items-center px-2 py-1 rounded bg-purple-500 hover:bg-purple-600 text-white ml-2"
                    >
                      <FontAwesomeIcon icon={faCopy} className="mr-1" />
                      Copy
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className={`${isDark ? "text-gray-300" : "text-gray-500"} mt-4`}>
          No leases found.
        </p>
      )}

      {/* Lease Modal */}
      {showLeaseModal && editingLease && (
        <div
          className={`fixed inset-0 flex items-center justify-center z-40 ${
            isDark ? "bg-black bg-opacity-80" : "bg-black bg-opacity-50"
          }`}
        >
          <div
            className={`w-full max-w-5xl p-4 rounded-lg shadow-2xl relative transition-colors duration-300 ${
              isDark ? "bg-gray-800 text-gray-100" : "bg-white text-gray-900"
            }`}
            style={{ maxHeight: "90vh", overflowY: "auto" }}
          >
            {/* Sticky header */}
            <div
              className={`sticky top-0 z-10 px-4 py-3 border-b flex justify-between items-center ${
                isDark
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              <h4 className="font-semibold text-sm">
                {editingLease.LeaseID || "(New)"} –{" "}
                {editingLease.LeaseName || "Unnamed Lease"}
              </h4>
              <button
                onClick={handleCancelLease}
                className="p-2 rounded-full hover:bg-gray-500/20"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            {/* Modal Title */}
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-lg font-bold">
                {editingLease.LeaseID &&
                leases.some((l) => l.LeaseID === editingLease.LeaseID)
                  ? "Edit Lease"
                  : "Create Lease"}
              </h4>
              <button
                onClick={handleCancelLease}
                className="p-2 rounded-full text-gray-600 hover:text-gray-900 dark:hover:text-gray-300"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            {/* Tab Buttons */}
            {(() => {
              const isInjection =
                (editingLease.WellType || "").toUpperCase() === "INJ";
              return (
                <div className="mb-4 flex flex-wrap gap-2">
                  <button
                    onClick={() => setActiveTab("basic")}
                    className={`px-3 py-1 rounded ${
                      activeTab === "basic"
                        ? "bg-blue-500 text-white"
                        : isDark
                          ? "bg-gray-700 text-gray-100"
                          : "bg-gray-300 text-gray-800"
                    }`}
                  >
                    Basic Info
                  </button>

                  <button
                    onClick={() => setActiveTab("additional")}
                    className={`px-3 py-1 rounded ${
                      activeTab === "additional"
                        ? "bg-blue-500 text-white"
                        : isDark
                          ? "bg-gray-700 text-gray-100"
                          : "bg-gray-300 text-gray-800"
                    }`}
                  >
                    Additional Info
                  </button>

                  {!isInjection && (
                    <button
                      onClick={() => setActiveTab("purchasers")}
                      className={`px-3 py-1 rounded ${
                        activeTab === "purchasers"
                          ? "bg-blue-500 text-white"
                          : isDark
                            ? "bg-gray-700 text-gray-100"
                            : "bg-gray-300 text-gray-800"
                      }`}
                    >
                      Purchasers
                    </button>
                  )}

                  {isInjection && (
                    <button
                      onClick={() => setActiveTab("injection")}
                      className={`px-3 py-1 rounded ${
                        activeTab === "injection"
                          ? "bg-blue-500 text-white"
                          : isDark
                            ? "bg-gray-700 text-gray-100"
                            : "bg-gray-300 text-gray-800"
                      }`}
                    >
                      Injection
                    </button>
                  )}

                  <button
                    onClick={() => setActiveTab("wells")}
                    className={`px-3 py-1 rounded ${
                      activeTab === "wells"
                        ? "bg-blue-500 text-white"
                        : isDark
                          ? "bg-gray-700 text-gray-100"
                          : "bg-gray-300 text-gray-800"
                    }`}
                  >
                    Wells
                  </button>

                  {!isInjection && (
                    <button
                      onClick={() => setActiveTab("tanks")}
                      className={`px-3 py-1 rounded ${
                        activeTab === "tanks"
                          ? "bg-blue-500 text-white"
                          : isDark
                            ? "bg-gray-700 text-gray-100"
                            : "bg-gray-300 text-gray-800"
                      }`}
                    >
                      Tanks
                    </button>
                  )}
                </div>
              );
            })()}
            {/* BASIC */}
            {activeTab === "basic" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4 text-sm">
                <div>
                  <label className="block mb-1 font-semibold">Lease ID</label>
                  <input
                    name="LeaseID"
                    value={editingLease.LeaseID}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                    readOnly={isEditMode}
                  />
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Lease Name</label>
                  <input
                    name="LeaseName"
                    value={editingLease.LeaseName}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  />
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Active?</label>
                  <div className="flex items-center gap-2">
                    <input
                      id="leaseActiveToggle"
                      type="checkbox"
                      name="Active"
                      checked={editingLease.Active === "Y"}
                      onChange={handleLeaseCheckboxChange}
                      className="accent-blue-600 dark:accent-blue-400"
                    />
                    <label htmlFor="leaseActiveToggle" className="font-medium">
                      Enabled
                    </label>
                  </div>
                </div>

                {/* Pumper, Route */}
                <div>
                  <label className="block mb-1 font-semibold">Pumper</label>
                  <select
                    name="PumperID"
                    value={editingLease.PumperID || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- Select --</option>
                    {pumperOptions
                      ?.filter((u) => u.UserID !== "admin")
                      .map((u) => (
                        <option key={u.UserID} value={u.UserID}>
                          {u.UserID} - {u.FullName}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">
                    Pumper Route
                  </label>
                  <input
                    type="number"
                    name="PumperRoute"
                    min="0"
                    step="1"
                    value={editingLease.PumperRoute ?? ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                    placeholder="e.g. 1, 2, 3…"
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                </div>

                {/* Relief */}
                <div>
                  <label className="block mb-1 font-semibold">Relief</label>
                  <select
                    name="ReliefID"
                    value={editingLease.ReliefID || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- Select --</option>
                    {reliefOptions
                      ?.filter((u) => u.UserID !== "admin")
                      .map((u) => (
                        <option key={u.UserID} value={u.UserID}>
                          {u.UserID} - {u.FullName}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Group, WellType */}
                <div>
                  <label className="block mb-1 font-semibold">Group</label>
                  <input
                    type="text"
                    name="GroupID"
                    value={editingLease.GroupID || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  />
                </div>

                <div>
                  <label className="block mb-1 font-semibold">WellType</label>
                  <select
                    name="WellType"
                    value={editingLease.WellType || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="OIL">OIL</option>
                    <option value="GAS">GAS</option>
                    <option value="INJ">INJ</option>
                    <option value="WSW">WSW</option>
                  </select>
                </div>

                {/* Show flags */}
                <div>
                  <label className="block mb-1 font-semibold">Show Oil?</label>
                  <div className="flex items-center gap-2">
                    <input
                      id="showOilToggle"
                      type="checkbox"
                      name="ShowOil"
                      checked={editingLease.ShowOil === "Y"}
                      onChange={handleLeaseCheckboxChange}
                      className="accent-blue-600 dark:accent-blue-400"
                    />
                    <label htmlFor="showOilToggle" className="font-medium">
                      Enable
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Show Gas?</label>
                  <div className="flex items-center gap-2">
                    <input
                      id="showGasToggle"
                      type="checkbox"
                      name="ShowGas"
                      checked={editingLease.ShowGas === "Y"}
                      onChange={handleLeaseCheckboxChange}
                      className="accent-blue-600 dark:accent-blue-400"
                    />
                    <label htmlFor="showGasToggle" className="font-medium">
                      Enable
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">
                    Show Water?
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="showWaterToggle"
                      type="checkbox"
                      name="ShowWater"
                      checked={editingLease.ShowWater === "Y"}
                      onChange={handleLeaseCheckboxChange}
                      className="accent-blue-600 dark:accent-blue-400"
                    />
                    <label htmlFor="showWaterToggle" className="font-medium">
                      Enable
                    </label>
                  </div>
                </div>

                {/* Water Meter, District */}
                <div>
                  <label className="block mb-1 font-semibold">
                    Use Water Meter?
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="useWaterMeter"
                      type="checkbox"
                      name="WaterMeter"
                      checked={editingLease.WaterMeter === "Y"}
                      onChange={handleLeaseCheckboxChange}
                      className="accent-blue-600 dark:accent-blue-400"
                    />
                    <label htmlFor="useWaterMeter" className="font-medium">
                      Enable
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">District</label>
                  <input
                    name="District"
                    value={editingLease.District || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  />
                </div>

                {/* RRC, CP, County, FieldName */}
                <div>
                  <label className="block mb-1 font-semibold">RRC</label>
                  <input
                    name="RRC"
                    value={editingLease.RRC || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  />
                </div>

                <div>
                  <label className="block mb-1 font-semibold">CP</label>
                  <input
                    name="CP"
                    value={editingLease.CP || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  />
                </div>

                <div>
                  <label className="block mb-1 font-semibold">County</label>
                  <input
                    name="County"
                    value={editingLease.County || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                    placeholder="e.g., Howard"
                  />
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Field Name</label>
                  <input
                    name="FieldName"
                    value={editingLease.FieldName || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                    placeholder="e.g., Spraberry"
                  />
                </div>
              </div>
            )}
            {/* ADDITIONAL (Tags/Investors/External Property #) */}
            {activeTab === "additional" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4 text-sm">
                <div>
                  <label className="block mb-1 font-semibold">Tag 1</label>
                  <select
                    name="Tag1"
                    value={editingLease.Tag1 || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- None --</option>
                    {tags?.map((t) => (
                      <option key={t.TagID} value={t.TagID}>
                        {t.TagDesc}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Tag 2</label>
                  <select
                    name="Tag2"
                    value={editingLease.Tag2 || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- None --</option>
                    {tags?.map((t) => (
                      <option key={t.TagID} value={t.TagID}>
                        {t.TagDesc}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Tag 3</label>
                  <select
                    name="Tag3"
                    value={editingLease.Tag3 || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- None --</option>
                    {tags?.map((t) => (
                      <option key={t.TagID} value={t.TagID}>
                        {t.TagDesc}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Tag 4</label>
                  <select
                    name="Tag4"
                    value={editingLease.Tag4 || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- None --</option>
                    {tags?.map((t) => (
                      <option key={t.TagID} value={t.TagID}>
                        {t.TagDesc}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Investor 1</label>
                  <select
                    name="Investor1"
                    value={editingLease.Investor1 || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- Select --</option>
                    {investorOptions?.map((u) => (
                      <option key={u.UserID} value={u.UserID}>
                        {u.UserID} - {u.FullName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Investor 2</label>
                  <select
                    name="Investor2"
                    value={editingLease.Investor2 || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- Select --</option>
                    {investorOptions?.map((u) => (
                      <option key={u.UserID} value={u.UserID}>
                        {u.UserID} - {u.FullName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">
                    External Property #
                  </label>
                  <input
                    name="PropertyNum"
                    value={editingLease.PropertyNum || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  />
                </div>
              </div>
            )}
            {/* PURCHASERS (hidden for INJ) */}
            {activeTab === "purchasers" &&
              (editingLease.WellType || "").toUpperCase() !== "INJ" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4 text-sm">
                  <div>
                    <label className="block mb-1 font-semibold">
                      Purchaser
                    </label>
                    <select
                      name="Purchaser"
                      value={editingLease.Purchaser || ""}
                      onChange={handleLeaseFieldChange}
                      className={getInputClasses(isDark)}
                    >
                      <option value="">-- Select --</option>
                      {purchasers?.map((p) => (
                        <option key={p.PurchaserID} value={p.PurchaserID}>
                          {p.PurchaserID} - {p.PurchaserName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block mb-1 font-semibold">
                      Purchaser Lease No
                    </label>
                    <input
                      name="PurchaserLeaseNo"
                      value={editingLease.PurchaserLeaseNo || ""}
                      onChange={handleLeaseFieldChange}
                      className={getInputClasses(isDark)}
                    />
                  </div>

                  <div>
                    <label className="block mb-1 font-semibold">
                      Water Transport
                    </label>
                    <input
                      name="WaterTransport"
                      value={editingLease.WaterTransport || ""}
                      onChange={handleLeaseFieldChange}
                      className={getInputClasses(isDark)}
                      placeholder="e.g., ABC Water Hauling"
                    />
                  </div>
                </div>
              )}
            {/* INJECTION (only when WellType == INJ) */}
            {activeTab === "injection" &&
              (editingLease.WellType || "").toUpperCase() === "INJ" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4 text-sm">
                  <div>
                    <label className="block mb-1 font-semibold">UIC</label>
                    <input
                      id="uicInput"
                      type="text"
                      name="UIC"
                      value={editingLease.UIC || ""}
                      onChange={handleLeaseFieldChange}
                      maxLength={9}
                      className={getInputClasses(isDark)}
                      placeholder="e.g., 123456789"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 font-semibold">
                      Daily Target
                    </label>
                    <input
                      type="number"
                      name="Target"
                      min="0"
                      step="1"
                      value={editingLease.Target ?? 0}
                      onChange={handleLeaseFieldChange}
                      className={getInputClasses(isDark)}
                      placeholder="0"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      title="Integer daily target (e.g., 0, 25, 300)"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 font-semibold">Max Inj</label>
                    <input
                      type="number"
                      name="MaxInj"
                      min="0"
                      step="1"
                      value={editingLease.MaxInj ?? 0}
                      onChange={handleLeaseFieldChange}
                      className={getInputClasses(isDark)}
                      placeholder="0"
                      inputMode="numeric"
                      pattern="[0-9]*"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 font-semibold">
                      Max Pressure
                    </label>
                    <input
                      type="number"
                      name="MaxPressure"
                      min="0"
                      step="1"
                      value={editingLease.MaxPressure ?? 0}
                      onChange={handleLeaseFieldChange}
                      className={getInputClasses(isDark)}
                      placeholder="0"
                      inputMode="numeric"
                      pattern="[0-9]*"
                    />
                  </div>
                </div>
              )}
            {/* WELLS (grid-style like leases) */}
            {activeTab === "wells" && (
              <div className="mb-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 mb-2">
                  <h5 className="font-semibold">Wells</h5>

                  <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                    <div className="relative">
                      <FontAwesomeIcon
                        icon={faSearch}
                        className={`absolute left-3 top-2.5 ${
                          isDark ? "text-gray-400" : "text-gray-500"
                        }`}
                      />
                      <input
                        type="text"
                        placeholder="Search wells..."
                        value={wellSearch}
                        onChange={(e) => setWellSearch(e.target.value)}
                        className={`pl-9 pr-4 py-1 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDark
                            ? "bg-gray-700 text-gray-100 border border-gray-600"
                            : "bg-white text-gray-700 border border-gray-300"
                        }`}
                      />
                    </div>

                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={showInactiveWells === true}
                        onChange={(e) => setShowInactiveWells(e.target.checked)}
                      />
                      <span
                        className={isDark ? "text-gray-200" : "text-gray-700"}
                      >
                        Show Inactive
                      </span>
                    </label>

                    <button
                      onClick={handleAddWell}
                      className="inline-flex items-center px-2 py-1 rounded bg-gray-300 hover:bg-gray-400 text-gray-800 whitespace-nowrap"
                    >
                      <FontAwesomeIcon icon={faPlus} className="mr-1" />
                      Add Well
                    </button>
                  </div>
                </div>

                {displayedWells.length > 0 ? (
                  <div className="w-full rounded-md shadow-sm overflow-hidden">
                    <table className="w-full table-auto text-sm">
                      <thead className={getHeaderClasses()}>
                        <tr>
                          <SortableHeader
                            label="WellID"
                            columnKey="WellID"
                            sortBy={wellSortBy}
                            sortDirection={wellSortDirection}
                            onSort={handleWellSort}
                            theme={theme}
                          />
                          <SortableHeader
                            label="Active"
                            columnKey="Active"
                            sortBy={wellSortBy}
                            sortDirection={wellSortDirection}
                            onSort={handleWellSort}
                            theme={theme}
                          />
                          <SortableHeader
                            label="External Well #"
                            columnKey="PropertyNum"
                            sortBy={wellSortBy}
                            sortDirection={wellSortDirection}
                            onSort={handleWellSort}
                            theme={theme}
                          />
                          <SortableHeader
                            label="Fixed Alloc Pct"
                            columnKey="AllocPct"
                            sortBy={wellSortBy}
                            sortDirection={wellSortDirection}
                            onSort={handleWellSort}
                            theme={theme}
                          />
                          <SortableHeader
                            label="API #"
                            columnKey="API"
                            sortBy={wellSortBy}
                            sortDirection={wellSortDirection}
                            onSort={handleWellSort}
                            theme={theme}
                          />
                          <SortableHeader
                            label="RRC"
                            columnKey="RRCLeaseID"
                            sortBy={wellSortBy}
                            sortDirection={wellSortDirection}
                            onSort={handleWellSort}
                            theme={theme}
                          />
                          <SortableHeader
                            label="Electric Meter #"
                            columnKey="ElectricMeter"
                            sortBy={wellSortBy}
                            sortDirection={wellSortDirection}
                            onSort={handleWellSort}
                            theme={theme}
                          />
                          <SortableHeader
                            label="CP #"
                            columnKey="CP"
                            sortBy={wellSortBy}
                            sortDirection={wellSortDirection}
                            onSort={handleWellSort}
                            theme={theme}
                          />
                          <th className="py-2 px-2 text-left whitespace-nowrap">
                            Actions
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {displayedWells.map(({ well, idx }) => (
                          <tr
                            key={well.UniqID || `well-${idx}`}
                            className={getRowClasses()}
                          >
                            <td className="py-2 px-2">
                              <input
                                className={`${getInputClasses(isDark)} w-full min-w-0`}
                                value={well.WellID || ""}
                                onChange={(e) =>
                                  handleWellChange(
                                    idx,
                                    "WellID",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>

                            <td className="py-2 px-2">
                              <select
                                className={`${getInputClasses(isDark)} w-full min-w-0`}
                                value={well.Active || "Y"}
                                onChange={(e) =>
                                  handleWellChange(
                                    idx,
                                    "Active",
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="Y">Yes</option>
                                <option value="N">No</option>
                              </select>
                            </td>

                            <td className="py-2 px-2">
                              <input
                                className={`${getInputClasses(isDark)} w-full min-w-0`}
                                value={well.PropertyNum || ""}
                                onChange={(e) =>
                                  handleWellChange(
                                    idx,
                                    "PropertyNum",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>

                            <td className="py-2 px-2">
                              <input
                                className={`${getInputClasses(isDark)} w-full min-w-0`}
                                value={well.AllocPct || "0.0000"}
                                onChange={(e) =>
                                  handleWellChange(
                                    idx,
                                    "AllocPct",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>

                            <td className="py-2 px-2">
                              <input
                                className={`${getInputClasses(
                                  isDark,
                                )} w-[11ch] max-w-full min-w-0 whitespace-nowrap`}
                                value={well.API || ""}
                                maxLength={11}
                                inputMode="numeric"
                                pattern="\d*"
                                onChange={(e) =>
                                  handleWellChange(idx, "API", e.target.value)
                                }
                              />
                            </td>

                            <td className="py-2 px-2">
                              <input
                                className={`${getInputClasses(isDark)} w-full min-w-0`}
                                value={well.RRCLeaseID || ""}
                                onChange={(e) =>
                                  handleWellChange(
                                    idx,
                                    "RRCLeaseID",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>

                            <td className="py-2 px-2">
                              <input
                                className={`${getInputClasses(isDark)} w-full min-w-0`}
                                value={well.ElectricMeter || ""}
                                maxLength={50}
                                onChange={(e) =>
                                  handleWellChange(
                                    idx,
                                    "ElectricMeter",
                                    e.target.value,
                                  )
                                }
                                placeholder="Meter #"
                              />
                            </td>

                            <td className="py-2 px-2">
                              <input
                                className={`${getInputClasses(isDark)} w-full min-w-0`}
                                value={well.CP || ""}
                                onChange={(e) =>
                                  handleWellChange(idx, "CP", e.target.value)
                                }
                              />
                            </td>

                            <td className="py-2 px-2 whitespace-nowrap">
                              <button
                                onClick={() => handleRemoveWell(well)}
                                className="inline-flex items-center px-2 py-1 rounded bg-red-500 hover:bg-red-600 text-white text-xs whitespace-nowrap"
                              >
                                <FontAwesomeIcon
                                  icon={faTrash}
                                  className="mr-1"
                                />
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {toast.visible && (
                      <div className="fixed inset-0 flex items-center justify-center z-50">
                        <div className="bg-red-500 text-white p-4 rounded shadow-lg">
                          <div className="flex items-center justify-between">
                            <span>{toast.message}</span>
                            <button
                              onClick={() =>
                                setToast({ ...toast, visible: false })
                              }
                              className="ml-4"
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm italic">No wells found.</p>
                )}
              </div>
            )}
            {/* TANKS (grid-style like leases) — hidden for INJ */}
            {activeTab === "tanks" &&
              (editingLease.WellType || "").toUpperCase() !== "INJ" && (
                <div className="mb-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                    <h5 className="font-semibold">Tanks</h5>

                    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                      <div className="relative">
                        <FontAwesomeIcon
                          icon={faSearch}
                          className={`absolute left-3 top-2.5 ${
                            isDark ? "text-gray-400" : "text-gray-500"
                          }`}
                        />
                        <input
                          type="text"
                          placeholder="Search tanks..."
                          value={tankSearch}
                          onChange={(e) => setTankSearch(e.target.value)}
                          className={`pl-9 pr-4 py-1 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            isDark
                              ? "bg-gray-700 text-gray-100 border border-gray-600"
                              : "bg-white text-gray-700 border border-gray-300"
                          }`}
                        />
                      </div>

                      <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={showInactiveTanks === true}
                          onChange={(e) =>
                            setShowInactiveTanks(e.target.checked)
                          }
                        />
                        <span
                          className={isDark ? "text-gray-200" : "text-gray-700"}
                        >
                          Show Inactive
                        </span>
                      </label>

                      <button
                        onClick={handleAddTank}
                        className="inline-flex items-center px-2 py-1 rounded bg-gray-300 hover:bg-gray-400 text-gray-800"
                      >
                        <FontAwesomeIcon icon={faPlus} className="mr-1" />
                        Add Tank
                      </button>
                    </div>
                  </div>

                  {displayedTanks.length > 0 ? (
                    <div className="overflow-x-auto rounded-md shadow-sm">
                      <table className="min-w-full text-sm">
                        <thead className={getHeaderClasses()}>
                          <tr>
                            <SortableHeader
                              label="TankID"
                              columnKey="TankID"
                              sortBy={tankSortBy}
                              sortDirection={tankSortDirection}
                              onSort={handleTankSort}
                              theme={theme}
                            />
                            <SortableHeader
                              label="Active"
                              columnKey="Active"
                              sortBy={tankSortBy}
                              sortDirection={tankSortDirection}
                              onSort={handleTankSort}
                              theme={theme}
                            />
                            <SortableHeader
                              label="Size"
                              columnKey="Size"
                              sortBy={tankSortBy}
                              sortDirection={tankSortDirection}
                              onSort={handleTankSort}
                              theme={theme}
                            />
                            <SortableHeader
                              label="BBLS/Inch"
                              columnKey="BBLSperInch"
                              sortBy={tankSortBy}
                              sortDirection={tankSortDirection}
                              onSort={handleTankSort}
                              theme={theme}
                            />
                            <SortableHeader
                              label="Type"
                              columnKey="TankType"
                              sortBy={tankSortBy}
                              sortDirection={tankSortDirection}
                              onSort={handleTankSort}
                              theme={theme}
                            />
                            <SortableHeader
                              label="GasCoeff"
                              columnKey="GasCoeff"
                              sortBy={tankSortBy}
                              sortDirection={tankSortDirection}
                              onSort={handleTankSort}
                              theme={theme}
                            />
                            <SortableHeader
                              label="Exclude Draws"
                              columnKey="ExcludeDrawsFromProd"
                              sortBy={tankSortBy}
                              sortDirection={tankSortDirection}
                              onSort={handleTankSort}
                              theme={theme}
                            />
                            <SortableHeader
                              label="External Tank #"
                              columnKey="WPTankNum"
                              sortBy={tankSortBy}
                              sortDirection={tankSortDirection}
                              onSort={handleTankSort}
                              theme={theme}
                            />
                            <th className="py-2 px-4 text-left">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayedTanks.map(({ tank, idx }) => (
                            <tr
                              key={tank.UniqID || `tank-${idx}`}
                              className={getRowClasses()}
                            >
                              <td className="py-2 px-4">
                                <input
                                  className={getInputClasses(isDark)}
                                  value={tank.TankID || ""}
                                  onChange={(e) =>
                                    handleTankChange(
                                      idx,
                                      "TankID",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td className="py-2 px-4">
                                <select
                                  className={`${getInputClasses(
                                    isDark,
                                  )} min-w-[5.25rem]`}
                                  value={tank.Active || "Y"}
                                  onChange={(e) =>
                                    handleTankChange(
                                      idx,
                                      "Active",
                                      e.target.value,
                                    )
                                  }
                                >
                                  <option value="Y">Yes</option>
                                  <option value="N">No</option>
                                </select>
                              </td>
                              <td className="py-2 px-4">
                                <input
                                  type="number"
                                  className={getInputClasses(isDark)}
                                  value={tank.Size ?? 0}
                                  onChange={(e) =>
                                    handleTankChange(
                                      idx,
                                      "Size",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td className="py-2 px-4">
                                <input
                                  type="number"
                                  step="0.001"
                                  className={getInputClasses(isDark)}
                                  value={tank.BBLSperInch || "0.000"}
                                  onChange={(e) =>
                                    handleTankChange(
                                      idx,
                                      "BBLSperInch",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td className="py-2 px-4">
                                <select
                                  className={`${getInputClasses(
                                    isDark,
                                  )} min-w-[5.75rem]`}
                                  value={tank.TankType || "T"}
                                  onChange={(e) =>
                                    handleTankChange(
                                      idx,
                                      "TankType",
                                      e.target.value,
                                    )
                                  }
                                >
                                  <option value="T">Tank</option>
                                  <option value="W">Water</option>
                                  <option value="F">Frac</option>
                                </select>
                              </td>
                              <td className="py-2 px-4">
                                <input
                                  type="number"
                                  className={getInputClasses(isDark)}
                                  value={tank.GasCoeff || "0.00"}
                                  onChange={(e) =>
                                    handleTankChange(
                                      idx,
                                      "GasCoeff",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td className="py-2 px-4">
                                <select
                                  className={getInputClasses(isDark)}
                                  value={tank.ExcludeDrawsFromProd || "N"}
                                  onChange={(e) =>
                                    handleTankChange(
                                      idx,
                                      "ExcludeDrawsFromProd",
                                      e.target.value,
                                    )
                                  }
                                >
                                  <option value="N">No</option>
                                  <option value="Y">Yes</option>
                                </select>
                              </td>
                              <td className="py-2 px-4">
                                <input
                                  className={getInputClasses(isDark)}
                                  value={tank.WPTankNum || ""}
                                  onChange={(e) =>
                                    handleTankChange(
                                      idx,
                                      "WPTankNum",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td className="py-2 px-4">
                                <button
                                  onClick={() => {
                                    const hasHistory =
                                      tank?.HasHistory === true ||
                                      (typeof tank?.HasHistory === "string" &&
                                        tank.HasHistory.toUpperCase() ===
                                          "Y") ||
                                      Number(
                                        tank?.HistoryCount ??
                                          tank?.HistoricalCount ??
                                          tank?.ProdCount ??
                                          tank?.DrawCount ??
                                          0,
                                      ) > 0;

                                    if (hasHistory) {
                                      setToast({
                                        visible: true,
                                        message:
                                          "Cannot remove this tank because it has historical data. Please set Active to 'No' instead.",
                                      });
                                      return;
                                    }

                                    handleRemoveTank(tank);
                                  }}
                                  className="inline-flex items-center px-2 py-1 rounded bg-red-500 hover:bg-red-600 text-white text-xs"
                                >
                                  <FontAwesomeIcon
                                    icon={faTrash}
                                    className="mr-1"
                                  />
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {toast.visible && (
                        <div className="fixed inset-0 flex items-center justify-center z-50">
                          <div className="bg-red-500 text-white p-4 rounded shadow-lg">
                            <div className="flex items-center justify-between">
                              <span>{toast.message}</span>
                              <button
                                onClick={() =>
                                  setToast({ ...toast, visible: false })
                                }
                                className="ml-4"
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm italic">No tanks found.</p>
                  )}
                </div>
              )}
            {/* Footer */}
            <div className="mt-3 flex justify-end space-x-2">
              <button
                onClick={handleCancelLease}
                className={`px-3 py-1 rounded ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-gray-100"
                    : "bg-gray-300 hover:bg-gray-400 text-gray-800"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveLease}
                className="px-3 py-1 rounded bg-blue-500 hover:bg-blue-600 text-white"
              >
                Save Lease
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Replace Pumper Modal */}
      {showReplacePumperModal && (
        <div
          className={`fixed inset-0 flex items-center justify-center z-50 ${
            isDark ? "bg-black bg-opacity-80" : "bg-black bg-opacity-40"
          }`}
        >
          <div
            className={`w-full max-w-md p-4 rounded-lg shadow-2xl relative ${
              isDark ? "bg-gray-800 text-gray-100" : "bg-white text-gray-900"
            }`}
          >
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-lg font-bold">Replace Pumper</h4>
              <button
                onClick={closeReplacePumper}
                className="p-2 rounded-full hover:bg-gray-500/20"
                disabled={replaceBusy}
                title="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <p className="text-sm mb-3">
              This will <strong>move all ACTIVE leases</strong> assigned to the
              current pumper to the new pumper.
            </p>

            <div className="grid grid-cols-1 gap-3 text-sm">
              <div>
                <label className="block mb-1 font-semibold">
                  Current Pumper
                </label>
                <select
                  value={replaceFromPumper}
                  onChange={(e) => setReplaceFromPumper(e.target.value)}
                  className={getInputClasses(isDark)}
                  disabled={replaceBusy}
                >
                  <option value="">-- Select --</option>
                  {pumperOptions?.map((u) => (
                    <option key={u.UserID} value={u.UserID}>
                      {u.UserID} - {u.FullName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-1 font-semibold">New Pumper</label>
                <select
                  value={replaceToPumper}
                  onChange={(e) => setReplaceToPumper(e.target.value)}
                  className={getInputClasses(isDark)}
                  disabled={replaceBusy}
                >
                  <option value="">-- Select --</option>
                  {pumperOptions?.map((u) => (
                    <option key={u.UserID} value={u.UserID}>
                      {u.UserID} - {u.FullName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={closeReplacePumper}
                className={`px-3 py-1 rounded ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-gray-100"
                    : "bg-gray-300 hover:bg-gray-400 text-gray-800"
                }`}
                disabled={replaceBusy}
              >
                Cancel
              </button>
              <button
                onClick={confirmReplacePumper}
                className="px-3 py-1 rounded bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-70"
                disabled={replaceBusy}
              >
                {replaceBusy ? "Replacing..." : "Replace"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * SortableHeader is a reusable table-header cell for sorting.
 * It shows an up/down icon if the column is currently sorted.
 */
function SortableHeader({
  label,
  columnKey,
  sortBy,
  sortDirection,
  onSort,
  theme,
}) {
  const isSortedColumn = sortBy === columnKey;
  const isDark = theme === "dark";

  return (
    <th
      className="py-2 px-4 text-left cursor-pointer select-none"
      onClick={() => onSort(columnKey)}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        {isSortedColumn && (
          <FontAwesomeIcon
            icon={sortDirection === "asc" ? faSortUp : faSortDown}
            className={isDark ? "text-gray-200" : "text-gray-600"}
          />
        )}
      </div>
    </th>
  );
}
export default CompanyDashboard;
